import { OrganizationType } from "../types/organization.type";
import type { OrganizationSchema } from "../types/organization.type";
import { hasPermission } from "./matching";
import { mergeFlatPermissionsForPlatformSave } from "./platform-permission-catalog";
import {
  type RoleOrgType,
  expandRoleForSites,
  isRoleOrgType,
} from "./role-templates";
import { mergeFlatPermissionsForOrgSave } from "./user-management";

export const PLATFORM_READ_ONLY_PERMISSIONS: readonly string[] = [
  "recyclee.admin.approval.list",
  "recyclee.admin.approval.read",
  "recyclee.admin.audit_log.list",
  "recyclee.admin.collection_site.read",
  "recyclee.admin.dashboard.list",
  "recyclee.admin.dashboard.read",
  "recyclee.admin.document.list",
  "recyclee.admin.esg_baseline_ef.read",
  "recyclee.admin.esg_calculation.list",
  "recyclee.admin.esg_calculation.read",
  "recyclee.admin.esg_emission_factor.list",
  "recyclee.admin.esg_emission_factor.read",
  "recyclee.admin.esg_utility_factor.list",
  "recyclee.admin.esg_utility_factor.read",
  "recyclee.admin.esg_mrv_methodology.list",
  "recyclee.admin.esg_mrv_methodology.read",
  "recyclee.admin.forms_submission.list",
  "recyclee.admin.forms_submission.read",
  "recyclee.admin.organization.list",
  "recyclee.admin.pickup_order.list",
  "recyclee.admin.pre_order.list",
  "recyclee.admin.pre_order.read",
  "recyclee.admin.user.list",
  "recyclee.admin.waste_catalog.list",
  "recyclee.admin.waste_catalog.read",
] as const;

export type PlatformPresetKind = "read_only" | "auditor" | "custom" | null;

export interface AuditorOrgInput {
  id: string;
  organization_type: OrganizationType;
}

const PLATFORM_WRITE_ACTION_PATTERN =
  /\.(create|write|delete|assign|register|send|approve|manage)$/;

export function isPlatformReadOnlyFromPermissions(permissionList: readonly string[]): boolean {
  if (permissionList.includes("*")) {
    return false;
  }
  const platformPerms = permissionList.filter((p) => p.startsWith("recyclee.admin."));
  if (platformPerms.length === 0) {
    return false;
  }
  const hasPlatformWrite = platformPerms.some((p) => PLATFORM_WRITE_ACTION_PATTERN.test(p));
  return !hasPlatformWrite;
}

export function hasPlatformReadOnlyBundle(permissionList: readonly string[]): boolean {
  return PLATFORM_READ_ONLY_PERMISSIONS.every((p) => hasPermission(permissionList, p));
}

function tenantOrgIdsFromPermissions(permissionList: readonly string[]): string[] {
  const ids = new Set<string>();
  for (const p of permissionList) {
    if (!p.startsWith("recyclee.") || p.startsWith("recyclee.admin.")) {
      continue;
    }
    const orgId = p.split(".")[1];
    if (orgId) {
      ids.add(orgId);
    }
  }
  return [...ids];
}

function tenantHasWritePermissions(permissionList: readonly string[], orgId: string): boolean {
  const prefix = `recyclee.${orgId}.`;
  return permissionList.some(
    (p) =>
      p.startsWith(prefix) &&
      (PLATFORM_WRITE_ACTION_PATTERN.test(p) || p === `recyclee.${orgId}.*`),
  );
}

function isAuditorLegacyDict(legacy: Record<string, string[]> | undefined): boolean {
  if (!legacy) {
    return false;
  }
  const orgIds = Object.keys(legacy).filter((id) => id !== "*");
  if (orgIds.length < 2) {
    return false;
  }
  return orgIds.every(
    (id) =>
      legacy[id]?.length === 1 &&
      legacy[id][0] === "admin_read_only",
  );
}

export function detectPlatformPreset(
  permissionList: readonly string[],
  permissionsLegacy?: Record<string, string[]>,
): PlatformPresetKind {
  if (permissionList.includes("*")) {
    return null;
  }
  const hasReadOnlyBundle = hasPlatformReadOnlyBundle(permissionList);
  if (!hasReadOnlyBundle) {
    return permissionList.some((p) => p.startsWith("recyclee.admin.")) ? "custom" : null;
  }
  const orgIds = tenantOrgIdsFromPermissions(permissionList);
  const auditorFromFlat =
    orgIds.length >= 2 && orgIds.every((id) => !tenantHasWritePermissions(permissionList, id));
  if (auditorFromFlat || isAuditorLegacyDict(permissionsLegacy)) {
    return "auditor";
  }
  return "read_only";
}

export function buildAuditorPermissions(
  organizations: readonly AuditorOrgInput[],
  sitesByOrg: Record<string, string[]> = {},
  existing: readonly string[] = [],
): string[] {
  let perms = mergeFlatPermissionsForPlatformSave(existing, PLATFORM_READ_ONLY_PERMISSIONS);
  for (const org of organizations) {
    if (!isRoleOrgType(org.organization_type)) {
      continue;
    }
    const orgType = org.organization_type as RoleOrgType;
    const siteIds: string[] | "*" =
      orgType === OrganizationType.WASTE_OUTLET
        ? sitesByOrg[org.id]?.length
          ? sitesByOrg[org.id]
          : ["*"]
        : "*";
    const expanded = expandRoleForSites(orgType, "admin_read_only", org.id, siteIds);
    perms = mergeFlatPermissionsForOrgSave(perms, org.id, expanded);
  }
  return perms;
}

export function mergePermissionsForPlatformReadOnlySave(
  existing: readonly string[],
): string[] {
  return mergeFlatPermissionsForPlatformSave(existing, PLATFORM_READ_ONLY_PERMISSIONS);
}

/** Flat list with platform read-only only — strips all tenant org permissions. */
export function buildPlatformReadOnlyOnlyPayload(
  grantable: readonly string[] = PLATFORM_READ_ONLY_PERMISSIONS,
): string[] {
  return [...grantable];
}

export function auditorOrgSitePayload(
  organizations: readonly AuditorOrgInput[],
): Record<string, string[]> {
  const sites: Record<string, string[]> = {};
  for (const org of organizations) {
    if (org.organization_type === OrganizationType.WASTE_OUTLET) {
      sites[org.id] = ["*"];
    }
  }
  return sites;
}

export function auditorRoleDict(
  organizations: readonly AuditorOrgInput[],
): Record<string, string[]> {
  const dict: Record<string, string[]> = {};
  for (const org of organizations) {
    if (isRoleOrgType(org.organization_type)) {
      dict[org.id] = ["admin_read_only"];
    }
  }
  return dict;
}

export async function fetchAllRoleOrganizations(
  getOrganizations: (params: { page: number; limit: number }) => Promise<{
    organizations?: OrganizationSchema[];
    total?: number;
  } | null>,
): Promise<AuditorOrgInput[]> {
  const all: OrganizationSchema[] = [];
  let page = 1;
  const limit = 100;
  while (true) {
    const result = await getOrganizations({ page, limit });
    const orgs = result?.organizations ?? [];
    all.push(...orgs);
    const total = result?.total ?? all.length;
    if (all.length >= total || orgs.length < limit) {
      break;
    }
    page += 1;
  }
  return all
    .filter((o) => isRoleOrgType(o.organization_type))
    .map((o) => ({ id: o.id, organization_type: o.organization_type }));
}
